import { useState } from 'react';
import { Settings, X } from 'lucide-react';
import {
  loadCompressionProfile,
  saveCompressionProfile,
  QUALITY_PRESETS,
  type CompressionProfile,
} from '../../../lib/assetCompression';

interface Props {
  onClose: () => void;
}

export default function AssetCompressionSettings({ onClose }: Props) {
  const [profile, setProfile] = useState<CompressionProfile>(loadCompressionProfile());

  const setPreset = (key: keyof typeof QUALITY_PRESETS) => {
    const next = QUALITY_PRESETS[key];
    setProfile(next);
    saveCompressionProfile(next);
  };

  const update = (patch: Partial<CompressionProfile>) => {
    const next = { ...profile, ...patch };
    setProfile(next);
    saveCompressionProfile(next);
  };

  return (
    <div className="absolute inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-[#141414] border border-white/[0.08] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Settings size={14} className="text-cyan-400" />
            <h3 className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">
              Compression
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X size={14} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 block">
              Quality Preset
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {(['aggressive', 'standard', 'high'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setPreset(k)}
                  className="px-2 py-1.5 text-[10px] rounded bg-white/[0.04] text-zinc-300 hover:bg-white/[0.08] border border-white/[0.06] capitalize transition-colors"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider">
                Max Dimension
              </label>
              <span className="text-[11px] text-cyan-400 font-mono">
                {profile.maxDimension}px
              </span>
            </div>
            <input
              type="range"
              min={800}
              max={4800}
              step={100}
              value={profile.maxDimension}
              onChange={(e) => update({ maxDimension: Number(e.target.value) })}
              className="w-full accent-cyan-500"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider">
                Quality
              </label>
              <span className="text-[11px] text-cyan-400 font-mono">
                {Math.round(profile.quality * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={0.4}
              max={0.98}
              step={0.02}
              value={profile.quality}
              onChange={(e) => update({ quality: Number(e.target.value) })}
              className="w-full accent-cyan-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={profile.preferWebp}
              onChange={(e) => update({ preferWebp: e.target.checked })}
              className="accent-cyan-500"
            />
            <span className="text-[11px] text-zinc-300">Prefer WebP output</span>
          </label>

          <p className="text-[10px] text-zinc-600 leading-relaxed">
            Larger images are downscaled to the max dimension and re-encoded at
            the selected quality. SVGs under 32KB are stored inline.
          </p>
        </div>
      </div>
    </div>
  );
}
